import React, { useCallback } from 'react';
import { FieldErrors, FieldValues } from 'react-hook-form';

import { NotificationGroupType, NotificationPropsType, useNotificationStore } from '~/stores/Notification';
import { Flex, Inner, Text } from '~/styles';

interface UseFormErrorHandlerOptions {
  defaultDisplayType?: NotificationGroupType;
}

interface HandleFormErrorsParams {
  errors: FieldValues;
  display?: NotificationGroupType;
  headline?: string;
}

interface HandleErrorsParams extends Partial<
  Pick<NotificationPropsType, 'display' | 'timeout' | 'actionButton' | 'variant'>
> {
  headline?: string;
}

export const getFormErrors = (errors: FieldErrors): string[] => {
  const errorMessages: string[] = [];

  const extractMessages = (errorObject: any) => {
    if (!errorObject) return;

    if (errorObject.hasOwnProperty('message') && errorObject.message.length > 0) {
      errorMessages.push(errorObject.message);
    } else {
      Object.values(errorObject).forEach((value) => {
        if (typeof value === 'object' && value !== null) {
          extractMessages(value);
        }
      });
    }
  };

  if (errors && typeof errors === 'object') {
    Object.values(errors).forEach(extractMessages);
  }

  return errorMessages;
};

export const useFormErrorHandler = ({ defaultDisplayType = 'global' }: UseFormErrorHandlerOptions = {}) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);

  const handleFormErrors = useCallback(
    ({ errors, display, headline }: HandleFormErrorsParams) => {
      const displayType = display || defaultDisplayType;

      if (!errors || typeof errors !== 'object') {
        return;
      }

      const errorMessages = getFormErrors(errors);

      if (errorMessages.length === 1) {
        addDangerNotification({
          display: displayType,
          content: errorMessages[0],
        });
      }

      if (errorMessages.length > 1) {
        addDangerNotification({
          display: displayType,
          content: (
            <>
              <Inner p={0} pb={4}>
                <Text size={14} color="background">
                  {headline || 'Fix the following errors:'}
                </Text>
              </Inner>
              <Flex flexDirection="column" gap={2}>
                {errorMessages.map((text) => (
                  <Text key={text} size={12} color="background">
                    • {text}
                  </Text>
                ))}
              </Flex>
            </>
          ),
        });
      }
    },
    [addDangerNotification, defaultDisplayType]
  );

  const handleSubmitErrors = useCallback(
    <T extends FieldValues>(errors: FieldErrors<T>) => {
      handleFormErrors({ errors, display: defaultDisplayType });
    },
    [handleFormErrors, defaultDisplayType]
  );

  const handleErrors = useCallback(
    (errors: string | string[], { display, headline, ...notificationProps }: HandleErrorsParams = {}) => {
      const displayType = display || defaultDisplayType;

      if (typeof errors === 'string') {
        addDangerNotification({
          display: displayType,
          content: errors,
          ...notificationProps,
        });

        return;
      }

      addDangerNotification({
        display: displayType,
        content: (
          <>
            <Inner p={0} pb={4}>
              <Text size={14} color="background">
                {headline || 'Please fix the following errors:'}
              </Text>
            </Inner>
            <Flex flexDirection="column" gap={2}>
              {errors.map((text) => (
                <Text key={text} size={12} color="background">
                  • {text}
                </Text>
              ))}
            </Flex>
          </>
        ),
        ...notificationProps,
      });
    },
    [addDangerNotification, defaultDisplayType]
  );

  return {
    handleFormErrors,
    handleErrors,
    handleSubmitErrors,
  };
};
